/**
 * useSlideTransition - Drive enter/exit animations for overlays.
 *
 * Returns `{ mounted, shown }`:
 *  - `mounted` controls whether the panel is in the DOM. Stays true during the
 *    exit animation so the slide-out plays before unmount.
 *  - `shown` controls the "open" visual state (translate / opacity targets).
 *
 * On open we need TWO animation frames between mount and `shown=true` so the
 * browser actually paints the closed state first; otherwise React batches both
 * commits and the panel appears instantly. On close we flip `shown` first so
 * the slide-out plays, then unmount after `durationMs`.
 */

import { useEffect, useState } from 'react';

function useSlideTransition(open, durationMs = 300) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id1 = requestAnimationFrame(() => {
        const id2 = requestAnimationFrame(() => setShown(true));
        cleanup.id2 = id2;
      });
      const cleanup = { id1, id2: null };
      return () => {
        cancelAnimationFrame(cleanup.id1);
        if (cleanup.id2 != null) cancelAnimationFrame(cleanup.id2);
      };
    }
    if (mounted) {
      setShown(false);
      const timeoutId = setTimeout(() => setMounted(false), durationMs);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [open, mounted, durationMs]);

  return { mounted, shown };
}

export default useSlideTransition;
